// resolveAudioAssetUrl — decide which URL a Play Audio node actually plays.
//
// A Play Audio node can name its clip three ways, and they have a strict
// precedence so authoring intent is never ambiguous:
//
//   1. a WIRED `in-url` pin — an upstream node (a literal string, a concat,
//      a variable) computed the URL this frame. It wins outright: a wire is a
//      deliberate override of whatever the node itself stores. (The singing
//      feature drives exactly this — a literal-string URL into in-url.)
//   2. `data.audioAssetId` — the node points at a CONTENT-BROWSER audio asset
//      by id. The URL is the server's `file_url` for that row, read live, never
//      derived on the client (CLAUDE.md: ask by id, play the URL the server
//      handed back). This is what the dropdown stores.
//   3. legacy `data.url` — a raw path typed into the old text box, e.g. the
//      bundled `/assets/audio/greeting.wav`, which is a public file and NOT a
//      server audio row. Kept working so shipped blueprints don't break.
//
// Pure and side-effect-free so it can be unit-tested and called from the hot
// exec path without importing the asset store here — the caller passes the live
// audio rows in. Returns { url, source, missingId } — `missingId` is set when an
// audioAssetId was chosen but no row matches it (a deleted asset, or the id
// belongs to another character), so the runtime can warn LOUDLY instead of
// silently playing nothing.

/**
 * @param {object} args
 * @param {string|null} [args.wiredUrl]   resolved value of the in-url pin, or null when unwired
 * @param {string|null} [args.audioAssetId]
 * @param {string|null} [args.url]        legacy raw path
 * @param {Array<{id:string,file_url?:string,name?:string}>} [args.audioAssets]  live server rows
 * @returns {{url: string, source: 'wired'|'asset'|'legacy'|'none', missingId: string|null}}
 */
export function resolveAudioAssetUrl({ wiredUrl = null, audioAssetId = null, url = null, audioAssets = [] } = {}) {
  if (wiredUrl != null && String(wiredUrl) !== '') {
    return { url: String(wiredUrl), source: 'wired', missingId: null }
  }
  if (audioAssetId) {
    const row = (audioAssets ?? []).find(a => a?.id === audioAssetId)
    if (row?.file_url) return { url: String(row.file_url), source: 'asset', missingId: null }
    // The id was set but the asset is gone / not this character's. Do NOT fall
    // through to legacy url — an author who picked an asset would be baffled to
    // hear a stale typed path instead. Report the miss so the runtime warns.
    return { url: '', source: 'none', missingId: audioAssetId }
  }
  if (url != null && String(url) !== '') {
    return { url: String(url), source: 'legacy', missingId: null }
  }
  return { url: '', source: 'none', missingId: null }
}
